/**
 * Visual editor for Mermaid diagrams using React Flow
 * Supports drag-drop node creation, edge connections, visual editing, and multi-select
 */

import type { DragEvent } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Background,
  Controls,
  type EdgeTypes,
  MiniMap,
  type NodeTypes,
  type OnSelectionChangeFunc,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
} from "@xyflow/react"

import "@xyflow/react/dist/style.css"

import { useTheme } from "@/app/providers/theme-provider"
import { cn } from "@/lib/utils"

import { getMermaidSvgNodePositions, type MermaidNodeShape } from "../utils"
import { MermaidEdge, MermaidNode } from "./nodes"
import { SelectionToolbar } from "./selection-toolbar"
import { useMermaidEditorStore } from "../stores/mermaid-editor-store"

const nodeTypes = {
  mermaid: MermaidNode,
} as NodeTypes

const edgeTypes = {
  mermaidEdge: MermaidEdge,
} as EdgeTypes

type MermaidVisualEditorCanvasProps = {
  readOnly?: boolean
}

function MermaidVisualEditorCanvas({ readOnly = false }: MermaidVisualEditorCanvasProps) {
  const {
    nodes,
    edges,
    code,
    isValid,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    undo,
    redo,
    canUndo,
    canRedo,
    selectedNodeIds,
    setSelectedNodeIds,
    deleteSelectedNodes,
    applyNodeLayout,
  } = useMermaidEditorStore()

  const { screenToFlowPosition } = useReactFlow()
  const { resolvedTheme: theme } = useTheme()
  const layoutTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const layoutRunRef = useRef(0)
  const lastLayoutKeyRef = useRef<string | null>(null)

  const [isModifierPressed, setIsModifierPressed] = useState(false)
  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC")

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) {
        setIsModifierPressed(true)
      }
    }
    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) {
        setIsModifierPressed(false)
      }
    }
    const handleBlur = () => setIsModifierPressed(false)

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    window.addEventListener("blur", handleBlur)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
      window.removeEventListener("blur", handleBlur)
    }
  }, [])

  const onSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes: selectedNodes }) => {
      setSelectedNodeIds(selectedNodes.map((node) => node.id))
    },
    [setSelectedNodeIds]
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (readOnly) return

      if ((event.ctrlKey || event.metaKey) && event.key === "z" && !event.shiftKey) {
        event.preventDefault()
        if (canUndo()) undo()
      }
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "y" || (event.key === "z" && event.shiftKey))
      ) {
        event.preventDefault()
        if (canRedo()) redo()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [undo, redo, canUndo, canRedo, readOnly])

  useEffect(() => {
    if (!isValid) return
    if (nodes.length === 0) return

    const trimmedCode = code.trim()
    if (!trimmedCode) return

    const currentTheme = theme === "dark" ? "dark" : "light"
    const layoutKey = `${currentTheme}:${trimmedCode}`
    if (lastLayoutKeyRef.current === layoutKey) return

    if (layoutTimeoutRef.current) {
      clearTimeout(layoutTimeoutRef.current)
    }

    layoutTimeoutRef.current = setTimeout(() => {
      layoutRunRef.current += 1
      const currentRunId = layoutRunRef.current

      void (async () => {
        try {
          const positions = await getMermaidSvgNodePositions(trimmedCode, { theme: currentTheme })
          if (layoutRunRef.current !== currentRunId) return
          if (Object.keys(positions).length === 0) return

          applyNodeLayout(positions)
          lastLayoutKeyRef.current = layoutKey
        } catch {
          // ignore layout errors (invalid/incomplete Mermaid code)
        }
      })()
    }, 350)

    return () => {
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current)
      }
    }
  }, [applyNodeLayout, code, isValid, nodes.length, theme])

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (readOnly) return
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"
    },
    [readOnly]
  )

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (readOnly) return
      event.preventDefault()

      const shape = event.dataTransfer.getData("application/mermaid-node/shape")
      const label = event.dataTransfer.getData("application/mermaid-node/label")

      if (!shape) return

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      addNode(shape as MermaidNodeShape, label || undefined, position)
    },
    [addNode, screenToFlowPosition, readOnly]
  )

  const defaultEdgeOptions = useMemo(
    () => ({
      style: { strokeWidth: 2, stroke: "hsl(var(--muted-foreground))" },
      type: "mermaidEdge",
      data: { style: "solid", sourceArrow: "none", targetArrow: "arrow" },
    }),
    []
  )

  return (
    <ReactFlow
      fitView
      defaultEdgeOptions={defaultEdgeOptions}
      deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
      edges={edges}
      edgeTypes={edgeTypes}
      nodes={nodes}
      nodesConnectable={!readOnly}
      nodesDraggable={!readOnly}
      nodeTypes={nodeTypes}
      panOnDrag={readOnly || !isModifierPressed}
      selectionMode={SelectionMode.Partial}
      selectionOnDrag={!readOnly && isModifierPressed}
      onConnect={readOnly ? undefined : onConnect}
      onDragOver={readOnly ? undefined : onDragOver}
      onDrop={readOnly ? undefined : onDrop}
      onEdgesChange={readOnly ? undefined : onEdgesChange}
      onNodesChange={readOnly ? undefined : onNodesChange}
      onSelectionChange={readOnly ? undefined : onSelectionChange}
    >
      <Controls className="!border-border !bg-background [&>button]:!border-border [&>button]:!bg-background [&>button]:!fill-foreground" />
      <MiniMap
        className="!border-border !bg-background"
        maskColor="hsl(var(--muted) / 0.5)"
        nodeColor="hsl(var(--muted))"
      />
      <Background gap={16} size={1} />
      <Panel position="top-right">
        <div className="bg-background/80 rounded border px-2 py-1 text-xs backdrop-blur">
          {nodes.length} nodes | {edges.length} edges
        </div>
      </Panel>

      {!readOnly && (
        <Panel position="bottom-left">
          <div className="bg-background/80 text-muted-foreground flex items-center gap-3 rounded border px-3 py-1.5 text-xs backdrop-blur">
            <span>Drag to pan</span>
            <span className="text-border">|</span>
            <span>{isMac ? "⌘" : "Ctrl"} + Drag to select</span>
            <span className="text-border">|</span>
            <span>{isMac ? "⌘" : "Ctrl"} + Z undo</span>
            <span className="text-border">|</span>
            <span>{isMac ? "⌘ + ⇧ + Z" : "Ctrl + Y"} redo</span>
          </div>
        </Panel>
      )}

      {!readOnly && selectedNodeIds.length > 0 && (
        <SelectionToolbar selectedCount={selectedNodeIds.length} onDelete={deleteSelectedNodes} />
      )}

      <svg className="absolute" height="0" width="0">
        <defs>
          <marker id="arrow" markerHeight="8" markerWidth="8" orient="auto" refX="8" refY="4">
            <path d="M0,0 L0,8 L8,4 Z" fill="hsl(var(--muted-foreground))" />
          </marker>
          <marker
            id="arrow-reverse"
            markerHeight="8"
            markerWidth="8"
            orient="auto"
            refX="0"
            refY="4"
          >
            <path d="M8,0 L8,8 L0,4 Z" fill="hsl(var(--muted-foreground))" />
          </marker>
          <marker id="circle" markerHeight="8" markerWidth="8" refX="4" refY="4">
            <circle cx="4" cy="4" fill="hsl(var(--muted-foreground))" r="3" />
          </marker>
          <marker id="cross" markerHeight="8" markerWidth="8" refX="4" refY="4">
            <path d="M1,1 L7,7 M1,7 L7,1" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
          </marker>
        </defs>
      </svg>
    </ReactFlow>
  )
}

type MermaidVisualEditorProps = {
  className?: string
  readOnly?: boolean
}

export function MermaidVisualEditor({ className, readOnly = false }: MermaidVisualEditorProps) {
  return (
    <div className={cn("h-full w-full", className)}>
      <ReactFlowProvider>
        <MermaidVisualEditorCanvas readOnly={readOnly} />
      </ReactFlowProvider>
    </div>
  )
}
